import {
  ChartPieIcon,
  DollarSignIcon,
  PencilIcon,
  PercentIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Transaction } from "@/apis/gql/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { TypographyCaption, TypographySmall } from "@/components/ui/typography";
import { calculatePaymentDiscrepancy } from "@/lib/ledger/journal-entry-utils";
import { cn, formatAmount } from "@/lib/utils";
import {
  TriggerTransactionBookingsClearingReason,
  useTriggerTransactionBookingsClearingReason,
} from "@/queries/transactions/trigger-transaction-bookings-clearing-reason";

import { ConfirmBankTransactionBookingsButton } from "../../shared/bank-bookings/confirm-bank-transaction-bookings-button";

type BookingOption = {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onSuccess?: () => void;
};

type Props = {
  transactions: Transaction[];
  currency: string;
  toBookingsTab: () => void;
  onConfirmBookings?: (bankTxnIds?: string[]) => Promise<void>;
};

export const BookingsReasonConfirmationFooter = ({
  transactions,
  currency = "EUR",
  toBookingsTab,
  onConfirmBookings,
}: Props) => {
  const [triggerTransactionBookingsClearingReason] =
    useTriggerTransactionBookingsClearingReason();

  const { t } = useTranslation();
  const [differenceReason, setDifferenceReason] = useState<
    TriggerTransactionBookingsClearingReason | undefined
  >(undefined);

  const { isOverpayment, isUnderpayment, isExactMatch, discrepancy } =
    calculatePaymentDiscrepancy(
      transactions.map((transaction) => ({
        invoices: transaction.invoices,
        side: transaction.side,
        amount: transaction.amount,
      })),
    );

  const matchedInvoices = useMemo(() => {
    return transactions.flatMap((transaction) => transaction.invoices);
  }, [transactions]);

  const options = useMemo(() => {
    if (isUnderpayment) {
      return {
        [TriggerTransactionBookingsClearingReason.DISCOUNT]: {
          label: t(
            "transactions.accounting.panel.booking.clearing-reason.cash-discount",
          ),
          icon: PercentIcon,
        },
        [TriggerTransactionBookingsClearingReason.FXGAIN]: {
          label: t(
            "transactions.accounting.panel.booking.clearing-reason.currency-difference",
          ),
          icon: DollarSignIcon,
        },
        [TriggerTransactionBookingsClearingReason.PARTIAL_PAYMENT]: {
          label: t(
            "transactions.accounting.panel.booking.clearing-reason.partial-payment",
          ),
          icon: ChartPieIcon,
        },
      };
    }
    if (isOverpayment) {
      return {
        [TriggerTransactionBookingsClearingReason.FXLOSS]: {
          label: t(
            "transactions.accounting.panel.booking.clearing-reason.currency-difference",
          ),
          icon: DollarSignIcon,
        },
        [TriggerTransactionBookingsClearingReason.MANUAL_ENTRY]: {
          label: t(
            "transactions.accounting.panel.booking.clearing-reason.manual-entry",
          ),
          icon: PencilIcon,
          onSuccess: toBookingsTab,
        },
      };
    }
    return {};
  }, [t, isOverpayment, isUnderpayment, toBookingsTab]);

  const option = options[differenceReason as keyof typeof options] as
    | BookingOption
    | undefined;

  const isDisabled = useMemo(() => {
    if (!isExactMatch) {
      return !differenceReason;
    }
    return false;
  }, [isExactMatch, differenceReason]);

  const handleOnSelect = async (
    value: TriggerTransactionBookingsClearingReason,
  ) => {
    setDifferenceReason(value as TriggerTransactionBookingsClearingReason);

    const option = options[value as keyof typeof options] as
      | BookingOption
      | undefined;

    // We need to confirm each transaction separately because the confirmation is done per transaction.
    // Multiple confirmations is not allowed yet, but we still want to support it in the future maybe.
    await Promise.all(
      transactions.map(async (transaction) => {
        return triggerTransactionBookingsClearingReason({
          bankTransactionId: transaction.bankTxnId,
          reason: value,
        });
      }),
    );

    option?.onSuccess?.();
  };

  const handleConfirmTransactionBooking = async () => {
    if (isExactMatch) {
      await Promise.all(
        transactions.map(async (transaction) => {
          return triggerTransactionBookingsClearingReason({
            bankTransactionId: transaction.bankTxnId,
            reason: undefined,
          });
        }),
      );
    }

    await onConfirmBookings?.(
      transactions.map((transaction) => transaction.bankTxnId),
    );
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex flex-row gap-2 w-full">
        <div className="flex-1 flex flex-row gap-4 items-center">
          <div className="flex flex-col">
            <TypographySmall
              className={cn(
                "font-bold font-mono text-base whitespace-nowrap",
                isExactMatch ? "text-emerald-600" : "text-black",
              )}
            >
              {formatAmount(Math.abs(discrepancy), currency)}
            </TypographySmall>
            <TypographyCaption className="text-xs">
              {isOverpayment
                ? t("transactions.accounting.panel.booking.status.positive")
                : t("transactions.accounting.panel.booking.status.negative")}
            </TypographyCaption>
          </div>
          {!isExactMatch && matchedInvoices.length > 0 && (
            <Select
              value={differenceReason}
              onValueChange={(value) =>
                handleOnSelect(
                  value as TriggerTransactionBookingsClearingReason,
                )
              }
            >
              <SelectTrigger className="w-[220px]">
                {differenceReason ? (
                  <div className="flex flex-row items-center gap-2">
                    {option?.icon && <option.icon className="w-4 h-4" />}
                    <span>{option?.label}</span>
                  </div>
                ) : (
                  <span className="text-muted-foreground">
                    {t(
                      "transactions.accounting.panel.booking.clearing-reason.placeholder",
                    )}
                  </span>
                )}
              </SelectTrigger>
              <SelectContent>
                {Object.entries(options).map(([key, value]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex flex-row items-center gap-2">
                      {value.icon && <value.icon className="w-4 h-4" />}
                      <span>{value.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        {onConfirmBookings && (
          <ConfirmBankTransactionBookingsButton
            disabled={isDisabled}
            onClick={handleConfirmTransactionBooking}
          />
        )}
      </div>
    </div>
  );
};
